import { useState, useEffect } from 'react';
import { getUsers, createUser, updateUser, deleteUser } from '../api/users';
import { getCompanies } from '../api/companies';
import { getDepartments } from '../api/departments';
import { useToast } from '../context/ToastContext';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Topbar } from '../layout/Topbar';
import type { User, Company, Department } from '../types';

export function UsersPage() {
  const { addToast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<User | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState('AGENT');
  const [formCompanyId, setFormCompanyId] = useState('');
  const [formDeptId, setFormDeptId] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const [u, c, d] = await Promise.all([
        getUsers(), getCompanies(), getDepartments(),
      ]);
      setUsers(u);
      setCompanies(c);
      setDepartments(d);
    } catch {
      addToast('error', 'Falha ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleNew = () => {
    setSelected(null);
    setFormName(''); setFormEmail(''); setFormPassword('');
    setFormRole('AGENT'); setFormCompanyId(companies[0]?.id || '');
    setFormDeptId(''); setFormIsActive(true);
  };

  const handleSelect = (u: User) => {
    setSelected(u);
    setFormName(u.name); setFormEmail(u.email);
    setFormPassword(''); setFormRole(u.role);
    setFormCompanyId(u.companyId || '');
    setFormDeptId(u.agentProfile?.departmentId || '');
    setFormIsActive(u.isActive);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formEmail.trim()) return;
    if (!selected && !formPassword.trim()) return;
    setSaving(true);
    try {
      if (selected) {
        const updated = await updateUser(selected.id, {
          name: formName, email: formEmail,
          password: formPassword || undefined,
          role: formRole, departmentId: formDeptId || undefined,
          isActive: formIsActive,
        });
        setUsers((prev) => prev.map((u) => u.id === updated.id ? updated : u));
        addToast('success', 'Usuário atualizado');
      } else {
        const created = await createUser({
          name: formName, email: formEmail, password: formPassword,
          role: formRole, companyId: formCompanyId, departmentId: formDeptId || undefined,
        });
        setUsers((prev) => [...prev, created]);
        addToast('success', 'Usuário criado');
      }
      handleNew();
    } catch {
      addToast('error', 'Falha ao salvar usuário');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
      if (selected?.id === id) setSelected(null);
      addToast('success', 'Usuário excluído');
    } catch {
      addToast('error', 'Falha ao excluir usuário');
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="view-container">
      <Topbar title="Usuários" subtitle="Agentes, administradores e permissões de acesso." />

      <div className="records-layout">
        <div className="panel">
          <div className="panel-header">
            <h2>Usuários ({users.length})</h2>
            <button onClick={handleNew}>+ Novo</button>
          </div>
          {loading ? (
            <div className="empty-state"><div className="spinner" /></div>
          ) : users.length === 0 ? (
            <div className="empty-state"><p>Nenhum usuário cadastrado.</p></div>
          ) : (
            <div className="data-list">
              {users.map((u) => (
                <div key={u.id} className={`data-list-item${selected?.id === u.id ? ' active' : ''}`}
                  onClick={() => handleSelect(u)}>
                  <div>
                    <div className="item-name">{u.name}</div>
                    <div className="item-detail">{u.email} — {u.role}</div>
                  </div>
                  <span className={`pill ${u.isActive ? 'success' : 'neutral'}`}>
                    {u.isActive ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="panel">
          <div className="panel-header">
            <h2>{selected ? 'Editar' : 'Novo'} usuário</h2>
          </div>
          <div className="panel-body">
            <div className="form-stack">
              <label>Nome <input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Nome completo" /></label>
              <label>E-mail <input value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="email@provedor.com" type="email" /></label>
              <label>Senha {selected ? '(deixe vazio para manter)' : ''}
                <input value={formPassword} onChange={(e) => setFormPassword(e.target.value)} type="password" placeholder={selected ? 'Nova senha' : 'Senha de acesso'} />
              </label>
              <label>Empresa
                <select value={formCompanyId} onChange={(e) => setFormCompanyId(e.target.value)}>
                  {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
              <label>Perfil
                <select value={formRole} onChange={(e) => setFormRole(e.target.value)}>
                  <option value="AGENT">Atendente</option>
                  <option value="COMPANY_ADMIN">Administrador</option>
                  <option value="SUPER_ADMIN">Super Admin</option>
                </select>
              </label>
              <label>Departamento
                <select value={formDeptId} onChange={(e) => setFormDeptId(e.target.value)}>
                  <option value="">Sem departamento</option>
                  {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
              <label className="checkbox-row" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={formIsActive} onChange={(e) => setFormIsActive(e.target.checked)} />
                Usuário ativo
              </label>
              <div className="flex gap-sm">
                <button onClick={handleSave} disabled={saving}>{saving ? 'Salvando...' : 'Salvar'}</button>
                <button className="secondary" onClick={handleNew}>Cancelar</button>
                {selected && (
                  <button className="danger" onClick={() => setConfirmDelete(selected.id)}>Excluir</button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir usuário"
        message="Tem certeza? Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        onConfirm={() => handleDelete(confirmDelete!)}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
